/**
 * Admin · Pinterest Performance
 *
 * Surfaces the scheduled-pins ledger from `db.metricool_scheduled_pins`,
 * aggregates by primary keyword + by internal board, and lists each pin
 * with the SEO metadata we already persist (title, link, board, keyword).
 *
 * Impression/save/click columns are placeholders that flip live once
 * `metrics_enrichment_enabled` is true (post-V1 backend extension).
 */
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, Loader2, ExternalLink, Send, Image as ImageIcon, AlertCircle, Upload, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    adminMetricoolPinterestPerformance,
    adminReplaceImageAsset,
    adminAIVariantImageAsset,
} from "@/lib/api";

const RANGE_OPTIONS = [
    { days: 7, label: "7 days" },
    { days: 30, label: "30 days" },
    { days: 90, label: "90 days" },
    { days: 365, label: "1 year" },
];

export default function AdminPinterestPerformance() {
    const [days, setDays] = useState(30);
    const [boardFilter, setBoardFilter] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            await Promise.resolve();
            if (!alive) return;
            setLoading(true);
            try {
                const r = await adminMetricoolPinterestPerformance({
                    days,
                    boardKey: boardFilter,
                });
                if (alive) setData(r);
            } catch (e) {
                if (alive) {
                    toast.error(`Load failed: ${e?.response?.data?.detail || e.message}`);
                    setData(null);
                }
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [days, boardFilter]);

    const enrichmentLive = data?.metrics_enrichment_enabled;

    return (
        <div
            className="min-h-screen bg-bl-bg text-bl px-4 py-6 sm:px-8"
            data-testid="admin-pinterest-performance"
        >
            <div className="max-w-6xl mx-auto space-y-6">
                <header className="space-y-2">
                    <Link
                        to="/admin/metricool"
                        className="inline-flex items-center gap-1 text-xs text-bl-muted hover:text-bl"
                    >
                        <ChevronLeft className="h-3 w-3" /> Back to Metricool Settings
                    </Link>
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                        <div>
                            <h1 className="font-display font-semibold text-3xl tracking-tight">
                                Pinterest Performance
                            </h1>
                            <p className="text-sm text-bl-muted">
                                Pins scheduled via Metricool, grouped by keyword and board.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            {RANGE_OPTIONS.map((opt) => (
                                <button
                                    key={opt.days}
                                    type="button"
                                    onClick={() => setDays(opt.days)}
                                    className={`text-xs px-2.5 py-1 rounded border ${
                                        days === opt.days
                                            ? "border-bl-primary bg-bl-primary/10 text-bl-primary"
                                            : "border-bl/30 text-bl-muted hover:border-bl/60"
                                    }`}
                                    data-testid={`perf-range-${opt.days}`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </header>

                {!enrichmentLive && (
                    <div
                        className="bl-card p-3 border-l-4 border-l-[var(--bl-amber)] text-xs flex items-start gap-2"
                        data-testid="perf-enrichment-banner"
                    >
                        <AlertCircle className="h-4 w-4 mt-0.5 text-[var(--bl-amber)]" />
                        <div>
                            <strong className="text-bl">Metrics enrichment not yet wired.</strong>
                            <p className="text-bl-muted mt-0.5">
                                Once the Metricool analytics endpoint is verified + connected, the impressions / saves / clicks columns will populate from a nightly sync. For now this dashboard shows the scheduled-pin ledger and SEO metadata only.
                            </p>
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-bl-muted">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading ledger…
                    </div>
                ) : !data ? (
                    <div className="bl-card p-5 text-sm text-bl-muted" data-testid="perf-empty-no-data">
                        No data available.
                    </div>
                ) : (
                    <>
                        {/* Summary tiles */}
                        <section
                            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
                            data-testid="perf-summary"
                        >
                            <Tile label="Pins scheduled" value={data.total_pins} />
                            <Tile label="Unique keywords" value={data.by_keyword.length} />
                            <Tile label="Boards in use" value={data.by_board.length} />
                            <Tile label="Range" value={`${data.range_days}d`} />
                        </section>

                        {/* By keyword */}
                        <section className="bl-card p-4 space-y-2" data-testid="perf-by-keyword">
                            <div className="flex items-center justify-between">
                                <h2 className="font-display font-semibold text-lg">By keyword</h2>
                                <span className="text-[11px] text-bl-muted">
                                    Sorted by impressions, then pin count.
                                </span>
                            </div>
                            {data.by_keyword.length === 0 ? (
                                <p className="text-sm text-bl-muted">
                                    No pins in this window. Schedule pins from the Images tab.
                                </p>
                            ) : (
                                <div className="overflow-x-auto border border-bl/30 rounded">
                                    <table className="w-full text-xs">
                                        <thead className="bg-bl-bg/60 text-bl-muted">
                                            <tr>
                                                <th className="text-left px-3 py-2">Keyword</th>
                                                <th className="text-right px-3 py-2">Pins</th>
                                                <th className="text-left px-3 py-2">Boards</th>
                                                <th className="text-right px-3 py-2">Impressions</th>
                                                <th className="text-right px-3 py-2">Saves</th>
                                                <th className="text-right px-3 py-2">Clicks</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.by_keyword.map((k) => (
                                                <tr
                                                    key={k.keyword}
                                                    className="border-t border-bl/20"
                                                    data-testid={`perf-keyword-row-${k.keyword}`}
                                                >
                                                    <td className="px-3 py-2 font-medium text-bl">{k.keyword}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{k.pin_count}</td>
                                                    <td className="px-3 py-2 text-[10px] text-bl-muted">
                                                        {k.board_keys.join(", ")}
                                                    </td>
                                                    <td className="px-3 py-2 text-right font-mono">{enrichmentLive ? k.total_impressions : "—"}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{enrichmentLive ? k.total_saves : "—"}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{enrichmentLive ? k.total_clicks : "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        {/* By board */}
                        <section className="bl-card p-4 space-y-2" data-testid="perf-by-board">
                            <div className="flex items-center justify-between">
                                <h2 className="font-display font-semibold text-lg">By board</h2>
                                <select
                                    value={boardFilter || ""}
                                    onChange={(e) => setBoardFilter(e.target.value || null)}
                                    className="text-xs border border-bl/30 rounded px-2 py-1 bg-bl-bg text-bl"
                                    data-testid="perf-board-filter"
                                >
                                    <option value="">All boards</option>
                                    {(data.boards_catalog || []).map((b) => (
                                        <option key={b.key} value={b.key}>
                                            {b.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {data.by_board.length === 0 ? (
                                <p className="text-sm text-bl-muted">No board distribution yet.</p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {data.by_board.map((b) => (
                                        <div
                                            key={b.board_key}
                                            className="bl-card p-3"
                                            data-testid={`perf-board-card-${b.board_key}`}
                                        >
                                            <div className="font-semibold text-sm text-bl truncate" title={b.board_name}>
                                                {b.board_name}
                                            </div>
                                            <div className="text-[10px] text-bl-muted font-mono mt-0.5">
                                                key · {b.board_key}
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                                                <Mini label="Pins" value={b.pin_count} />
                                                <Mini label="Impr" value={enrichmentLive ? b.total_impressions : "—"} />
                                                <Mini label="Saves" value={enrichmentLive ? b.total_saves : "—"} />
                                                <Mini label="Clicks" value={enrichmentLive ? b.total_clicks : "—"} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </section>

                        {/* Pin ledger */}
                        <section className="bl-card p-4 space-y-2" data-testid="perf-pin-ledger">
                            <h2 className="font-display font-semibold text-lg">Scheduled pins · ledger</h2>
                            {data.pins.length === 0 ? (
                                <p className="text-sm text-bl-muted" data-testid="perf-ledger-empty">
                                    No pins scheduled in this window yet. From any content package&apos;s Images tab, click <strong>Send to Pinterest</strong>.
                                </p>
                            ) : (
                                <div className="overflow-x-auto border border-bl/30 rounded">
                                    <table className="w-full text-xs">
                                        <thead className="bg-bl-bg/60 text-bl-muted">
                                            <tr>
                                                <th className="text-left px-3 py-2">When</th>
                                                <th className="text-left px-3 py-2">Image</th>
                                                <th className="text-left px-3 py-2">Title</th>
                                                <th className="text-left px-3 py-2">Keyword</th>
                                                <th className="text-left px-3 py-2">Board</th>
                                                <th className="text-right px-3 py-2">Impr</th>
                                                <th className="text-right px-3 py-2">Saves</th>
                                                <th className="text-right px-3 py-2">Clicks</th>
                                                <th className="text-right px-3 py-2">Link</th>
                                                <th className="text-right px-3 py-2">Metricool</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.pins.map((p, i) => (
                                                <PinRow
                                                    key={`${p.package_id}-${p.angle_key}-${i}`}
                                                    pin={p}
                                                    enrichmentLive={enrichmentLive}
                                                    onReplaced={() => {
                                                        // Bust the <img> cache so the new bytes show
                                                        // even though the URL is the same.
                                                        setData((prev) => prev
                                                            ? { ...prev, _imageBust: Date.now() }
                                                            : prev,
                                                        );
                                                    }}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </div>
        </div>
    );
}

function Tile({ label, value }) {
    return (
        <div className="bl-card p-3" data-testid={`perf-tile-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-[10px] uppercase tracking-wider text-bl-muted">{label}</div>
            <div className="font-display font-semibold text-2xl text-bl">{value}</div>
        </div>
    );
}

function Mini({ label, value }) {
    return (
        <div>
            <div className="text-[9px] uppercase tracking-wider text-bl-muted">{label}</div>
            <div className="font-mono text-sm text-bl">{value}</div>
        </div>
    );
}

function PinRow({ pin, enrichmentLive, onReplaced }) {
    const p = pin;
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [aiVarBusy, setAiVarBusy] = useState(false);
    const [cacheBust, setCacheBust] = useState(0);

    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const imgSrc = p.asset_id
        ? `${apiBase}/api/content-images/${p.asset_id}${cacheBust ? `?v=${cacheBust}` : ""}`
        : null;

    const handlePick = () => fileInputRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !p.asset_id) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Pick an image file (PNG, JPEG, WebP, GIF).");
            return;
        }
        setUploading(true);
        try {
            await adminReplaceImageAsset(p.package_id, p.asset_id, file);
            const next = Date.now();
            setCacheBust(next);
            toast.success("Pin image replaced.");
            onReplaced?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Replace failed.");
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleAIVariant = async () => {
        if (!p.asset_id || aiVarBusy) return;
        const hint = window.prompt(
            `Describe how the new AI variant should differ from the current pin.\n\nExamples:\n  • "make it more clinical, less playful"\n  • "use a warmer color palette, sunset tones"\n  • "tighter framing on the inspector's clipboard"`,
            "",
        );
        if (!hint || !hint.trim()) return;
        setAiVarBusy(true);
        try {
            await adminAIVariantImageAsset(p.package_id, p.asset_id, hint.trim());
            setCacheBust(Date.now());
            toast.success("AI variant generated.");
            onReplaced?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "AI variant failed.");
        } finally {
            setAiVarBusy(false);
        }
    };

    return (
        <tr
            className="border-t border-bl/20"
            data-testid={`perf-pin-row-${p.package_id}-${p.angle_key}`}
        >
            <td className="px-3 py-2 text-[10px] text-bl-muted font-mono whitespace-nowrap">
                {(p.scheduled_at || "").slice(0, 10)}
            </td>
            <td className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                    {imgSrc ? (
                        // Thumbnail doubles as the click target for replacement.
                        <button
                            type="button"
                            onClick={handlePick}
                            disabled={uploading}
                            title={uploading ? "Uploading…" : "Click to replace image"}
                            className="relative shrink-0 group"
                            data-testid={`perf-pin-replace-${p.asset_id}`}
                        >
                            <img
                                src={imgSrc}
                                alt={p.title || "pin"}
                                className="h-10 w-10 rounded object-cover border border-bl/30 group-hover:border-bl-primary transition-colors"
                            />
                            <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 rounded transition-opacity">
                                {uploading ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin text-white" />
                                ) : (
                                    <Upload className="h-3.5 w-3.5 text-white" />
                                )}
                            </span>
                        </button>
                    ) : (
                        <div className="h-10 w-10 rounded border border-bl/30 bg-bl-bg flex items-center justify-center text-bl-muted">
                            <ImageIcon className="h-4 w-4" />
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        className="hidden"
                        onChange={handleFile}
                        data-testid={`perf-pin-replace-input-${p.asset_id || "noasset"}`}
                    />
                    {p.asset_id && (
                        <button
                            type="button"
                            onClick={handleAIVariant}
                            disabled={aiVarBusy || uploading}
                            title="Generate an AI variant with a tweak hint"
                            className="bl-btn-ghost text-[10px] gap-0.5 px-1.5 py-1 border border-bl/30"
                            data-testid={`perf-pin-aivariant-${p.asset_id}`}
                        >
                            {aiVarBusy ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Sparkles className="h-3 w-3" />
                            )}
                            variant
                        </button>
                    )}
                </div>
            </td>
            <td className="px-3 py-2 text-bl max-w-xs truncate" title={p.title}>
                {p.title}
            </td>
            <td className="px-3 py-2 text-bl">{p.primary_keyword}</td>
            <td className="px-3 py-2 text-bl-muted text-[10px]">{p.board_name}</td>
            <td className="px-3 py-2 text-right font-mono">{enrichmentLive ? p.impressions : "—"}</td>
            <td className="px-3 py-2 text-right font-mono">{enrichmentLive ? p.saves : "—"}</td>
            <td className="px-3 py-2 text-right font-mono">{enrichmentLive ? p.clicks : "—"}</td>
            <td className="px-3 py-2 text-right">
                {p.destination_url ? (
                    <a
                        href={p.destination_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-bl-primary hover:underline inline-flex items-center gap-0.5"
                    >
                        <ExternalLink className="h-3 w-3" />
                    </a>
                ) : (
                    <span className="text-bl-muted">—</span>
                )}
            </td>
            <td className="px-3 py-2 text-right">
                {p.metricool_post_id ? (
                    <a
                        href={`https://app.metricool.com/posts/edit/${p.metricool_post_id}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Open this scheduled pin in Metricool"
                        className="text-bl-primary hover:underline inline-flex items-center gap-0.5 text-[10px]"
                        data-testid={`perf-pin-metricool-link-${p.metricool_post_id}`}
                    >
                        <Send className="h-3 w-3" /> open
                    </a>
                ) : (
                    <span className="text-bl-muted text-[10px]">—</span>
                )}
            </td>
        </tr>
    );
}
